import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { has, isEmpty } from "lodash";
import PropTypes from "prop-types";
import classNames from "classnames";
import ReactTooltip from "react-tooltip";
import moment from "moment";
import {
  updateChat,
  getTree,
  clearSelectedFile,
  resetHttpStatus
} from "store/actions/fileAction";
import Notification, { notifyApiError } from "components/Notification";
import SubmitButton from "components/Forms/Buttons/Submit";
import Textarea from "components/Forms/Elements/Textarea";
import Translate from "helpers/Translate";
import fileStatus from "helpers/fileStatus.json";

function Chat({ isHidden }) {
  const dispatch = useDispatch();
  const selectedClient = useSelector(state => state.client.selectedClient);
  const fileInfo = useSelector(state => state.file.getFile);
  const updateFileError = useSelector(state => state.file.errorFile);
  const updateFileResponseStatus = useSelector(state => state.file.httpStatus);
  const isLoading = useSelector(state => state.file.isLoadingFile);
  const selectedSubordinate = useSelector(state => state.file.getSubordinate);

  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (updateFileError) {
      setSubmitting(false);
      notifyApiError(updateFileError);
    }
  }, [updateFileError, updateFileResponseStatus, dispatch, fileInfo]);

  useEffect(() => {
    if (updateFileResponseStatus === 201) {
      setSubmitting(false);
      Notification.success(Translate({ id: "message_has_been_sent" }));
      dispatch(clearSelectedFile(null));
      dispatch(resetHttpStatus(null));
      dispatch(
        getTree({
          client: selectedClient,
          fileStatus: fileStatus.STATUS_DISCUSSIONS,
          subordinateId: selectedSubordinate?.id || ""
        })
      );
    }
  }, [updateFileResponseStatus, selectedSubordinate, selectedClient, dispatch]);

  const onSubmit = async e => {
    setSubmitting(true);
    dispatch(
      updateChat({
        fileId: fileInfo.id,
        message
      })
    );
  };

  const canSubmit = () => {
    if (
      has(fileInfo, "status") &&
      [fileStatus.STATUS_REJECTED, fileStatus.STATUS_DISCUSSIONS].includes(
        fileInfo.status
      )
    ) {
      return true;
    }
    return false;
  };

  return (
    <div
      className={classNames("tab-content-wrapper", {
        hide: isHidden
      })}
    >
      <ReactTooltip />
      <div className="tab-content tab-content-four selected">
        <h2 className="title my-20">
          {Translate({ id: "conversation" })} :{" "}
          <sup>({Translate({ id: "newest_first" })} )</sup>
        </h2>

        {has(fileInfo, "chat") && isEmpty(fileInfo.chat) && (
          <div className="alert alert-info">
            {Translate({
              id: "this_is_the_very_beginning_of_your_direct_message_history"
            })}
          </div>
        )}

        <ul className="chat">
          {has(fileInfo, "chat") &&
            !isEmpty(fileInfo.chat) &&
            fileInfo.chat.map(item => (
              <li key={item.created_at} className="mb-20">
                <div className="timestamp">
                  {moment(item.created_at).format("MMMM Do YYYY, H:mm")}
                </div>
                <div className="my-10">
                  <strong
                    data-tip={`${item.author.username} (${item.author.email})`}
                  >
                    {item.author.fullname}
                  </strong>
                </div>
                <div className="my-10">
                  <p>{item.message}</p>
                </div>
              </li>
            ))}
        </ul>
        {canSubmit() ? (
          <>
            <div className="form-group my-10">
              <Textarea
                id="message"
                name="message"
                onChange={e => setMessage(e.target.value)}
                fieldValue={message}
                errors={updateFileError}
                errorKey="message"
                placeholder={Translate({ id: "type_your_message" })}
                classes="chat-input"
                formErrorClass="form-error-inverse"
              />
            </div>
            <div className="form-group my-20 pb-50">
              <SubmitButton
                isLoading={isLoading || submitting}
                className="btn btn-tools fr"
                buttonIconClass="fas fa-paper-plane mr-5"
                buttonText={Translate({ id: "send_and_close" })}
                btnId="send"
                handleSubmit={onSubmit}
              />
            </div>
            <div className="my-30">&nbsp;</div>
          </>
        ) : (
          <div className="alert alert-info my-40">
            {Translate({ id: "you_cannot_send_message_at_this_stage" })}.
          </div>
        )}
      </div>
    </div>
  );
}

Chat.propType = {
  isHidden: PropTypes.bool
};

export default Chat;
